import YatungDymanicTabs from '@Src/_basic/components/YatungDymanicTabs';
import { ThirdPartyApiColumns } from '@Src/_basic/object/ThirdPartyAccountType';
import { Box, Card, CardContent, CardHeader, Checkbox, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const I18N_PREFIX = 'USERSMANAGEMENU.THIRD_PARTY_ACCOUNT_MANAGEMENT';

interface Props {
  apiColumns: Array<ThirdPartyApiColumns>;
  // key 為API路徑, value 為勾選的欄位代碼
  selectedColumns: Record<string, Array<string>>;
  onChange: (apiPath: string, columnNames: Array<string>) => void;
}

const titleStyle = {
  fontSize: 20,
  fontFamily: 'Inter',
  fontWeight: 900,
  color: '#FFFFFF',
};
const bodyStyle = {
  fontSize: 18,
  fontFamily: 'Inter',
  fontWeight: 400,
  color: '#697EA3',
  wordBreak: 'break-all' as const,
};

// API路徑轉為頁籤名稱的 i18n key, ex : /thirdparty/shipment-detail -> SHIPMENT_DETAIL
const toApiTitleKey = (apiPath: string) =>
  `${I18N_PREFIX}.API_TITLE.${(apiPath.split('/').pop() ?? '').replace(/-/g, '_').toUpperCase()}`;

// 編輯第三方廠商帳號：設定帳號呼叫 /thirdparty 底下各支API時回傳的資料欄位
export default function ThirdPartyApiColumnSettings({ apiColumns, selectedColumns, onChange }: Props) {
  const { t: i18T } = useTranslation();
  const [tabValue, setTabValue] = useState<number>(0);

  useEffect(() => {
    if (tabValue >= apiColumns.length) setTabValue(0);
  }, [apiColumns, tabValue]);

  const currentApi = apiColumns[tabValue];
  const columnNames = currentApi?.column_names ?? [];
  const checkedColumns = currentApi ? selectedColumns[currentApi.api_path] ?? [] : [];
  const checkedCount = columnNames.filter((columnName) => checkedColumns.includes(columnName)).length;

  const handleToggle = (columnName: string) => {
    if (!currentApi) return;
    const next = checkedColumns.includes(columnName)
      ? checkedColumns.filter((item) => item !== columnName)
      : [...checkedColumns, columnName];
    // 依API回傳物件中的欄位順序排列
    onChange(
      currentApi.api_path,
      columnNames.filter((item) => next.includes(item)),
    );
  };

  const handleToggleAll = () => {
    if (!currentApi) return;
    onChange(currentApi.api_path, checkedCount === columnNames.length ? [] : [...columnNames]);
  };

  return (
    <Stack>
      <Typography sx={{ fontSize: 20, fontWeight: 900, mb: 2 }}>{i18T(`${I18N_PREFIX}.API_COLUMN_SETTING`)}</Typography>
      <Box sx={{ backgroundColor: '#F2F2F2', p: 2 }}>
        <Grid container item xs={12} justifyContent={'flex-end'}>
          <YatungDymanicTabs
            tabs={apiColumns.map((api, index) => ({
              id: index,
              name: i18T(toApiTitleKey(api.api_path), { defaultValue: api.api_path }),
            }))}
            selectedIndex={tabValue}
            onChange={(_event, newValue: number) => setTabValue(newValue)}
            fontSize={16}
          />
        </Grid>
        <Card sx={{ width: '100%' }}>
          <CardHeader
            sx={{ background: '#003087', py: 1 }}
            title={
              <Grid container alignItems={'center'}>
                <Grid item xs={1}>
                  <Checkbox
                    sx={{ color: '#FFFFFF', '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#FFFFFF' } }}
                    checked={columnNames.length > 0 && checkedCount === columnNames.length}
                    indeterminate={checkedCount > 0 && checkedCount < columnNames.length}
                    disabled={columnNames.length === 0}
                    onChange={handleToggleAll}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Typography sx={titleStyle}>{i18T(`${I18N_PREFIX}.API_COLUMN_CODE`)}</Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography sx={titleStyle}>{i18T(`${I18N_PREFIX}.API_COLUMN_NAME`)}</Typography>
                </Grid>
              </Grid>
            }
          />
          <CardContent className="scroll-bar" sx={{ height: '520px', overflowY: 'auto', py: 1 }}>
            {columnNames.length === 0 ? (
              <Typography sx={{ ...bodyStyle, textAlign: 'center', mt: 2 }}>
                {i18T(`${I18N_PREFIX}.API_COLUMN_NO_DATA`)}
              </Typography>
            ) : (
              columnNames.map((columnName) => (
                <Grid
                  container
                  key={columnName}
                  alignItems={'center'}
                  onClick={() => handleToggle(columnName)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom: '1px solid #E0E0E0',
                    '&:hover': { backgroundColor: '#F7F9FC' },
                  }}
                >
                  <Grid item xs={1}>
                    <Checkbox
                      checked={checkedColumns.includes(columnName)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => handleToggle(columnName)}
                    />
                  </Grid>
                  <Grid item xs={4} sx={{ pr: 1 }}>
                    <Typography sx={bodyStyle}>{columnName}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography sx={bodyStyle}>
                      {i18T(`${I18N_PREFIX}.API_COLUMN.${columnName}`, { defaultValue: '' })}
                    </Typography>
                  </Grid>
                </Grid>
              ))
            )}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}
